/**
 * Keeps the free gift in sync with the cart.
 *
 * A theme cannot make a line free, only a Shopify discount can. This script puts the gift product in the cart once
 * the customer qualifies; an automatic "buy X get Y" discount is what zeroes its price. Because those are two
 * independent moving parts, the script verifies the outcome: if the gift line turns out not to be free, it takes the
 * gift back out rather than leaving the customer to pay for something the cart advertised as free.
 *
 * Whether the customer qualifies is decided in Liquid (snippets/free-shipping-bar via sections/cart-gift-state) and
 * read back over the Section Rendering API. That is deliberate: cart.js line items carry no collection information,
 * so the rules could not be evaluated here without duplicating them and letting the two drift apart.
 */

const ROOT = window.Shopify?.routes?.root ?? '/';
const SECTION = 'cart-gift-state';
const GIFT_PROPERTY = '_gift';

/**
 * Set once the gift turned out not to be free. Without it, the add / verify / remove path would loop: the customer
 * still qualifies, so the gift would go straight back in, fail the price check, come out again, and so on.
 * It holds the time and expires after a few minutes, so a discount that is fixed (or a cart that changed, e.g. refill
 * jars that now count) gets another try instead of the gift staying out for the whole visit.
 */
const BROKEN_DISCOUNT_KEY = 'cocovaye:gift-discount-unavailable:v3';
const BROKEN_DISCOUNT_RETRY_MS = 5 * 60 * 1000;

let syncing = false;
let syncAgain = false;

function discountIsKnownBroken() {
  try {
    const markedAt = Number(sessionStorage.getItem(BROKEN_DISCOUNT_KEY)) || 0;
    return Date.now() - markedAt < BROKEN_DISCOUNT_RETRY_MS;
  } catch (error) {
    // Private browsing and blocked site data both throw here. Treat it as "not broken" and rely on the price check.
    return false;
  }
}

function markDiscountBroken() {
  try {
    sessionStorage.setItem(BROKEN_DISCOUNT_KEY, String(Date.now()));
  } catch (error) {
    // Nothing to do: the price check below still prevents the wrong charge, it just runs again next cart change.
  }
}

async function readState() {
  const response = await fetch(`${ROOT}?sections=${SECTION}`);

  if (!response.ok) return null;

  const sections = await response.json();
  const markup = sections[SECTION];

  if (!markup) return null;

  const element = new DOMParser()
    .parseFromString(markup, 'text/html')
    .querySelector('[data-cart-gift-state]');

  if (!element) return null;

  return {
    enabled: element.dataset.enabled === 'true',
    claimable: element.dataset.claimable === 'true',
    variantId: element.dataset.variantId,
    subscriptionPlanId: element.dataset.subscriptionPlanId || null,
    giftPlanId: element.dataset.giftPlanId || null,
    lineKey: element.dataset.lineKey || null,
    managedByApp: element.dataset.managedByApp === 'true',
    linePrice: Number.parseInt(element.dataset.linePrice, 10) || 0,
  };
}

/**
 * Mirrors how the theme mutates the cart: collect the sections that want re-rendering, post, then announce the new
 * cart so the drawer, the cart count and the reward bar all update themselves.
 */
async function mutateCart(endpoint, body) {
  const sections = [];

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
  );

  const response = await fetch(`${ROOT}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, sections: sections.join(',') }),
  });

  if (!response.ok) return null;

  const cart = await response.json();

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'cart-gift', cart } })
  );

  return cart;
}

const addGift = (variantId, subscriptionPlanId) =>
  mutateCart('cart/add.js', {
    items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_PROPERTY]: 'true', ...(subscriptionPlanId ? { _addon_subscription: subscriptionPlanId } : {}) } }],
  });

const removeGift = (lineKey) => mutateCart('cart/change.js', { id: lineKey, quantity: 0 });

async function sync() {
  if (syncing) {
    syncAgain = true;
    return;
  }

  syncing = true;

  try {
    const state = await readState();

    // An app puts the gift in the cart and makes it free (Montie free gifts, settings.cart_gift_added_by_app): the
    // theme only shows the milestone and leaves the gift line alone
    if (!state || !state.enabled || !state.variantId || state.managedByApp) return;

    let mutated = null;

    if (state.lineKey && state.claimable && state.subscriptionPlanId && state.giftPlanId !== state.subscriptionPlanId) {
      // Progus links the one-time add-on discount to the qualifying selling plan.
      mutated = await removeGift(state.lineKey);
    } else if (state.lineKey && state.claimable && state.linePrice > 0) {
      // The gift is in the cart but not free, so the discount is missing, expired or no longer matches.
      markDiscountBroken();
      mutated = await removeGift(state.lineKey);
      console.warn(
        '[cart-gift] The gift line was not free and has been removed. Check the automatic discount in Shopify.'
      );
    } else if (state.claimable && !state.lineKey && !discountIsKnownBroken()) {
      mutated = await addGift(state.variantId, state.subscriptionPlanId);
    } else if (!state.claimable && state.lineKey) {
      mutated = await removeGift(state.lineKey);
    }

    // The cart page reloads on every line change instead of re-rendering sections, so a cart it did not trigger
    // itself leaves stale markup on screen. One reload settles it, and cannot loop: the state is consistent by then.
    if (mutated && window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
    }
  } catch (error) {
    console.warn('[cart-gift] Could not sync the free gift.', error);
  } finally {
    syncing = false;

    if (syncAgain) {
      syncAgain = false;
      sync();
    }
  }
}

document.addEventListener('cart:change', sync);
document.addEventListener('cart:refresh', sync);
sync();
